/**
 * web/src/pages/HelpSubmit.tsx
 * ヘルプスタッフ本人向けの公開「勤務・日報提出フォーム」（/help-submit/:token）。
 *
 * ログイン不要・完全公開ページ。api/client.ts の request()（401時に/loginへ
 * リダイレクトする既存ログイン前提処理）は使わず、専用の軽量fetch(api.helpSubmit)
 * のみを使う。単価・確定交通費・確定経費・確定残業などの金額情報は一切表示しない。
 *
 * 4セクション構成: 1.勤務情報 / 2.作業日報（work_details, 通常アシスタントと同一フォーマット）
 *                 / 3.経費申告 / 4.作業画像
 *
 * status遷移: pending -> draft（一時保存、何度でも可） -> submitted（最終提出）
 * 一時保存は pending/draft の間のみ。submitted以降は最終提出（再提出）APIのみで更新する。
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { HelpSubmitData } from '../api/client'
import { WorkDetailsEditor } from '../components/WorkDetailsEditor'
import { emptyDetail, isKaikaiKiki, type WorkDetail } from '../components/reportUtils'
import ColorModeToggleButton from '../components/ColorModeToggleButton'

// 通常アシスタントの勤怠入力（AssistantAttendance.tsx）と同じ考え方:
// 標準勤務時間510分（8.5時間）を超えた分を残業とする。サーバー側でも同じ式で必ず再計算される。
const NORMAL_PAID_MINUTES = 510

function diffMinutes(start: string, end: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(start)
  const n = /^(\d{1,2}):(\d{2})$/.exec(end)
  if (!m || !n) return 0
  const s = Number(m[1]) * 60 + Number(m[2])
  const e = Number(n[1]) * 60 + Number(n[2])
  return Math.max(0, e - s)
}

function computeOvertimeMinutes(start: string, end: string, breakMinutes: number): number {
  if (!start || !end) return 0
  const worked = diffMinutes(start, end) - Math.max(0, breakMinutes)
  return Math.max(0, worked - NORMAL_PAID_MINUTES)
}

function keyFromUrl(url: string): string {
  const marker = '/api/upload/file/'
  const idx = url.indexOf(marker)
  return idx >= 0 ? url.slice(idx + marker.length) : url
}

type ImageItem = { url: string; key: string }

export default function HelpSubmit() {
  const { token } = useParams<{ token: string }>()

  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [data, setData] = useState<HelpSubmitData | null>(null)

  // 状態遷移（一時保存でローカルに更新する。confirmed/paidは別スクリーンで扱うためここでは pending/draft/submitted のみ）
  const [status, setStatus] = useState<'pending' | 'draft' | 'submitted'>('pending')
  const [everSubmitted, setEverSubmitted] = useState(false)

  // 勤務情報
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [breakMinutes, setBreakMinutes] = useState('90')
  const [workNote, setWorkNote] = useState('')

  // 日報
  const [details, setDetails] = useState<WorkDetail[]>([emptyDetail()])
  const [reportIssues, setReportIssues] = useState('')

  // 経費申告
  const [transportFee, setTransportFee] = useState('')
  const [otherExpense, setOtherExpense] = useState('')
  const [expenseNote, setExpenseNote] = useState('')

  // 作業画像
  const [images, setImages] = useState<ImageItem[]>([])
  const [uploading, setUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const [dirty, setDirty] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [savingFinal, setSavingFinal] = useState(false)
  const [finalSaved, setFinalSaved] = useState(false)
  const [draftMessage, setDraftMessage] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [detailsError, setDetailsError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return }
    (async () => {
      try {
        const res = await api.helpSubmit.get(token)
        setData(res)
        setStatus(res.status === 'confirmed' ? 'submitted' : res.status)
        setEverSubmitted(res.submitted)
        setStartTime(res.start_time ?? '')
        setEndTime(res.end_time ?? '')
        // 休憩時間の初期値: 一度も保存されていない(pending)場合のみ90分をデフォルトにする
        // （10:00-20:00勤務・休憩0分のままだと意図せず残業90分と計算されるため）
        const initialBreak = res.status === 'pending' && !res.break_minutes ? 90 : (res.break_minutes ?? 0)
        setBreakMinutes(String(initialBreak))
        setWorkNote(res.work_note ?? '')
        setDetails(res.work_details?.length ? res.work_details : [emptyDetail()])
        setReportIssues(res.report_issues ?? '')
        setTransportFee(res.reported_transport_fee != null ? String(res.reported_transport_fee) : '')
        setOtherExpense(res.reported_other_expense != null ? String(res.reported_other_expense) : '')
        setExpenseNote(res.expense_note ?? '')
        setImages(res.image_urls.map((url) => ({ url, key: keyFromUrl(url) })))
      } catch {
        setNotFound(true)
      } finally {
        setLoading(false)
      }
    })()
  }, [token])

  // 未保存のまま離脱しようとした場合の警告:
  // ・未入力後、一時保存も最終提出もしていない場合: 警告
  // ・一時保存後、追加変更がなければ: 警告なし
  // ・一時保存後に再編集した場合: 警告
  // ・最終提出後: 警告なし
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirty || finalSaved) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty, finalSaved])

  const markDirty = () => { setDirty(true); setDraftMessage(null) }

  const nonNegInt = (v: string): number | undefined => {
    if (v.trim() === '') return undefined
    const n = Number(v)
    return Number.isInteger(n) && n >= 0 ? n : NaN
  }

  const computedOvertime = computeOvertimeMinutes(startTime, endTime, Number(breakMinutes) || 0)

  // ── 画像アップロード ──────────────────────────────────────────
  const uploadFiles = async (files: File[]) => {
    if (!token) return
    const imageFiles = files.filter((f) => f.type.startsWith('image/'))
    if (!imageFiles.length) return
    setUploading(true)
    setErr(null)
    try {
      for (const file of imageFiles) {
        const res = await api.helpSubmit.uploadImage(token, file)
        setImages((prev) => [...prev, { url: res.url, key: res.key }])
      }
    } catch (ex: any) {
      setErr(ex.message ?? '画像のアップロードに失敗しました')
    } finally {
      setUploading(false)
    }
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await uploadFiles(Array.from(e.target.files ?? []))
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    uploadFiles(Array.from(e.dataTransfer.files))
  }
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true) }
  const handleDragLeave = () => setIsDragging(false)

  const handleImageDelete = async (key: string) => {
    if (!token) return
    try {
      await api.helpSubmit.deleteImage(token, key)
      setImages((prev) => prev.filter((img) => img.key !== key))
    } catch (ex: any) {
      setErr(ex.message ?? '画像の削除に失敗しました')
    }
  }

  // ── 保存共通処理 ──────────────────────────────────────────────
  const buildBody = () => {
    const cleanDetails = details.filter((d) =>
      isKaikaiKiki(d.work_id, d.work_title)
        ? (d.memo ?? '').trim().length > 0
        : (d.work_title ?? '').trim().length > 0
    )
    const breakMin = nonNegInt(breakMinutes)
    const fee = nonNegInt(transportFee)
    const expense = nonNegInt(otherExpense)
    return { cleanDetails, breakMin, fee, expense }
  }

  const validateNumeric = (breakMin: number | undefined, fee: number | undefined, expense: number | undefined) => {
    if ([breakMin, fee, expense].some((n) => n !== undefined && Number.isNaN(n))) {
      setErr('休憩時間・交通費・その他経費は0以上の整数で入力してください')
      return false
    }
    return true
  }

  // ── 一時保存（pending/draftのときのみ） ─────────────────────────
  const handleSaveDraft = async () => {
    if (!token) return
    setErr(null)
    setDetailsError(undefined)
    const { cleanDetails, breakMin, fee, expense } = buildBody()
    if (!validateNumeric(breakMin, fee, expense)) return

    setSavingDraft(true)
    try {
      await api.helpSubmit.saveDraft(token, {
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        break_minutes: breakMin,
        work_note: workNote,
        work_details: cleanDetails,
        report_issues: reportIssues,
        reported_transport_fee: fee,
        reported_other_expense: expense,
        expense_note: expenseNote,
      })
      setStatus('draft')
      setDirty(false)
      setDraftMessage('一時保存しました。あとで同じリンクから続きが入力できます。')
    } catch (ex: any) {
      setErr(ex.message ?? '一時保存に失敗しました')
    } finally {
      setSavingDraft(false)
    }
  }

  // ── 最終提出（再提出も同じAPI） ────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setErr(null)
    setDetailsError(undefined)

    const { cleanDetails, breakMin, fee, expense } = buildBody()
    if (cleanDetails.length === 0) {
      setDetailsError('少なくとも1件の作業内容を入力してください')
      return
    }
    if (!validateNumeric(breakMin, fee, expense)) return

    setSavingFinal(true)
    try {
      await api.helpSubmit.submit(token, {
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        break_minutes: breakMin,
        work_note: workNote,
        work_details: cleanDetails,
        report_issues: reportIssues,
        reported_transport_fee: fee,
        reported_other_expense: expense,
        expense_note: expenseNote,
      })
      setFinalSaved(true)
      setDirty(false)
    } catch (ex: any) {
      setErr(ex.message ?? '送信に失敗しました')
    } finally {
      setSavingFinal(false)
    }
  }

  const cardStyle: React.CSSProperties = {
    width: '100%', maxWidth: 560,
    background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
    borderRadius: 14, padding: 'clamp(24px, 6vw, 40px) clamp(18px, 6vw, 32px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
    position: 'relative',
  }

  const pageStyle: React.CSSProperties = {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--paper)', padding: '20px 16px', boxSizing: 'border-box',
  }

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 13, fontWeight: 700, color: 'var(--ink-1)',
    marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--paper-3)',
  }

  const inputStyle: React.CSSProperties = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    height: 44, padding: '0 12px', fontSize: 15, background: 'var(--paper)', color: 'var(--ink)',
    border: '1.5px solid var(--paper-3)', borderRadius: 8, outline: 'none', appearance: 'none',
  }

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 5 }

  const themeToggle = (
    <div className="help-submit-theme-toggle">
      <ColorModeToggleButton />
    </div>
  )

  if (loading) {
    return <div style={pageStyle}><div style={{ color: 'var(--ink-3)' }}>読み込み中...</div></div>
  }

  if (notFound || !data) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div className="help-submit-header">
            {themeToggle}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>⚠</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>このリンクは利用できません</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.7 }}>
                リンクの有効期限が切れているか、無効化・確認済みの可能性があります。<br />
                担当者にお問い合わせください。
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (data.paid) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div className="help-submit-header">
            {themeToggle}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>お支払いが完了しています</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                このヘルプ勤務はすでに支払処理が完了しているため、内容の変更はできません。
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (data.confirmed) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div className="help-submit-header">
            {themeToggle}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>管理者が確認済みです</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                このヘルプ勤務は管理者が内容を確認済みのため、このリンクからの修正はできません。<br />
                修正が必要な場合は担当者にご連絡ください。
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (finalSaved) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div className="help-submit-header">
            {themeToggle}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>勤務・日報を提出しました。ありがとうございます。</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                内容の修正が必要な場合は、このページを再度開いて送信し直してください。
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const canSaveDraft = status === 'pending' || status === 'draft'

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div className="help-submit-header">
          {themeToggle}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 20, color: 'var(--ink)' }}>
              {data.help_staff_name}さんの勤務・日報提出フォーム
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
              勤務情報・作業日報・経費申告を入力してください
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6 }}>
              勤務日: {data.work_date}
            </div>
            {everSubmitted && (
              <div style={{ fontSize: 12, color: 'var(--blue)', marginTop: 8 }}>
                ✓ 提出済みです。内容を修正して再送信できます。
              </div>
            )}
            {status === 'draft' && !everSubmitted && (
              <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 8 }}>
                一時保存中です。まだ最終提出されていません。
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* ── 1. 勤務情報 ── */}
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>勤務情報</div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>勤務日</label>
              <input type="date" value={data.work_date} readOnly disabled
                style={{ ...inputStyle, background: 'var(--paper-3)', color: 'var(--ink-3)' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>開始時刻</label>
                <input type="time" value={startTime}
                  onChange={(e) => { setStartTime(e.target.value); markDirty() }} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>終了時刻</label>
                <input type="time" value={endTime}
                  onChange={(e) => { setEndTime(e.target.value); markDirty() }} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>休憩時間（分）</label>
                <input type="number" min={0} value={breakMinutes}
                  onChange={(e) => { setBreakMinutes(e.target.value); markDirty() }} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>自動計算された残業時間</label>
                <div style={{
                  ...inputStyle, display: 'flex', alignItems: 'center',
                  background: 'var(--paper-3)', color: 'var(--ink-2)', fontWeight: 600,
                }}>
                  {computedOvertime}分
                </div>
              </div>
            </div>
            <div>
              <label style={labelStyle}>勤務備考</label>
              <textarea value={workNote} rows={2}
                onChange={(e) => { setWorkNote(e.target.value); markDirty() }}
                placeholder="勤務にあたっての補足があれば入力してください"
                style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
            </div>
          </div>

          {/* ── 2. 作業日報 ── */}
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>作業日報</div>
            <WorkDetailsEditor
              details={details}
              onChange={(d) => { setDetails(d); markDirty() }}
              works={data.works}
              errorMessage={detailsError}
            />
            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>引継ぎ・連絡事項</label>
              <textarea value={reportIssues} rows={3}
                onChange={(e) => { setReportIssues(e.target.value); markDirty() }}
                placeholder="次に引き継ぐ内容や連絡事項があれば入力してください（特になければ「特になし」）"
                style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
            </div>
          </div>

          {/* ── 3. 経費申告 ── */}
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>経費申告</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>実費交通費（円）</label>
                <input type="number" min={0} value={transportFee}
                  onChange={(e) => { setTransportFee(e.target.value); markDirty() }}
                  placeholder="例：750" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>その他経費（円）</label>
                <input type="number" min={0} value={otherExpense}
                  onChange={(e) => { setOtherExpense(e.target.value); markDirty() }}
                  placeholder="例：1200" style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>経費メモ</label>
              <textarea value={expenseNote} rows={2}
                onChange={(e) => { setExpenseNote(e.target.value); markDirty() }}
                placeholder="領収書・経費の内容など"
                style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
            </div>
          </div>

          {/* ── 4. 作業画像 ── */}
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>作業画像（任意 / 複数可）</div>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              style={{
                border: `2px dashed ${isDragging ? 'var(--green)' : 'var(--paper-3)'}`,
                borderRadius: 10,
                background: isDragging ? 'rgba(45,122,58,0.04)' : 'var(--paper)',
                padding: '12px 14px',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: images.length > 0 ? 10 : 0 }}>
                {images.map((img) => (
                  <div key={img.key} style={{ position: 'relative' }}>
                    <img src={img.url}
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--paper-3)' }} />
                    <button type="button" onClick={() => handleImageDelete(img.key)}
                      style={{
                        position: 'absolute', top: -6, right: -6,
                        width: 20, height: 20, borderRadius: '50%',
                        background: 'var(--red)', color: '#fff', border: 'none',
                        cursor: 'pointer', fontSize: 11,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>✕</button>
                  </div>
                ))}
                <label style={{
                  width: 80, height: 80, borderRadius: 6,
                  border: '2px dashed var(--paper-3)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  color: 'var(--ink-3)', fontSize: 11, gap: 4,
                  background: uploading ? 'var(--paper-3)' : 'var(--paper)',
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: 20 }}>{uploading ? '⏳' : '+'}</span>
                  <span>{uploading ? 'UP中' : '追加'}</span>
                  <input type="file" accept="image/*" multiple onChange={handleImageSelect}
                    disabled={uploading} style={{ display: 'none' }} />
                </label>
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                JPEG / PNG / WebP / GIF・各5MBまで・最大10枚
              </div>
            </div>
          </div>

          {err && (
            <div style={{
              background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)',
              borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 20,
            }}>
              ⚠ {err}
            </div>
          )}

          {draftMessage && (
            <div style={{
              background: 'var(--green-light)', border: '1px solid rgba(45,122,58,0.2)',
              borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--green)', marginBottom: 20,
            }}>
              ✓ {draftMessage}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            {canSaveDraft && (
              <button type="button" className="btn btn-outline" onClick={handleSaveDraft}
                disabled={savingDraft || savingFinal}
                style={{ flex: 1, height: 48, fontSize: 14, fontWeight: 600, borderRadius: 10 }}>
                {savingDraft ? '保存中...' : '一時保存する'}
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={savingDraft || savingFinal}
              style={{ flex: 1, height: 48, fontSize: 15, fontWeight: 600, borderRadius: 10 }}>
              {savingFinal ? '送信中...' : everSubmitted ? '勤務・日報を再提出する' : '勤務・日報を提出する'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
